import React, { useState, useRef, useEffect, useMemo } from "react";

const PLUGIN_OPTIONS = [
    { id: "PUBMED_CENTRAL", label: "PubMed Central" },
    { id: "UNPAYWALL", label: "Unpaywall" },
    { id: "GALAXY_API", label: "Galaxy API" },
];

const PLUGIN_FILTER_OPTIONS = [
    { id: "ALL", label: "All" },
    ...PLUGIN_OPTIONS,
];

const ACCESS_STATUS_OPTIONS = [
    { id: "OPEN_ACCESS", label: "Open Access" },
    { id: "ORDER_PLACED", label: "Order Placed" },
    { id: "PURCHASED", label: "Purchased" },
    { id: "UNPURCHASED", label: "Unpurchased" },
    { id: "UNKNOWN", label: "Unknown" },
];

const ALL_ACCESS_IDS = ACCESS_STATUS_OPTIONS.map((o) => o.id);

const renderAccessBadge = (status, url, onViewPdf, title, deliveryPrice, paperId, onGalaxyOrder, orderType = "PDF") => {
    if (!status || status === "UNKNOWN") {
        return <span className="discovery-badge-unknown">Unknown</span>;
    }

    if (status === "OPEN_ACCESS") {
        return (
            <span
                className="discovery-badge-green cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    if (url && onViewPdf) onViewPdf(url, title);
                }}
            >
                Open Access
                {url && <i className="bi bi-box-arrow-up-right"></i>}
            </span>
        );
    }

    if (status === "ORDER_PLACED") {
        return (
            <span className="discovery-badge-orange">Order Placed</span>
        );
    }

    if (status === "PURCHASED") {
        return (
            <span
                className="discovery-badge-green cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation();
                    if (url && onViewPdf) onViewPdf(url, title);
                }}
            >
                Purchased
                {url && <i className="bi bi-box-arrow-up-right"></i>}
            </span>
        );
    }

    if (status === "UNPURCHASED") {
        if (deliveryPrice != null) {
            return (
                <span
                    className="discovery-badge-orange cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        onGalaxyOrder?.(paperId, orderType);
                    }}
                >
                    Buy at ${deliveryPrice.toFixed(2)}
                </span>
            );
        }
        return <span className="discovery-badge-unknown">Unknown</span>;
    }

    return <span className="discovery-badge-neutral">{status}</span>;
};

const ResultsList = ({
    results,
    selectedResult,

    selectedIds,
    selectAll,
    onSelectAll,
    onSelectOne,

    onSelectResult,
    onSearchPlugin,
    onViewPdf,
    onGalaxyOrder,
    onFilteredResultsChange,
    pluginFilter = "ALL",
    onPluginFilterChange,
    fullWidth = false,
    queryEditorOpen = false,
    onViewArticle,

    searchProcessingStatus,
    onRunSearch,

    // Filter bar props (fullWidth only)
    limit,
    onLimitChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    onClearFilters,

    // Show-selected toggle — controlled by parent; fall back to local state
    showSelectedOnly: showSelectedOnlyProp,
    setShowSelectedOnly: setShowSelectedOnlyProp,
}) => {
    const [rowPlugins, setRowPlugins] = useState({});
    const [openPluginRowId, setOpenPluginRowId] = useState(null);
    const [pluginFilterOpen, setPluginFilterOpen] = useState(false);
    const [accessFilterOpen, setAccessFilterOpen] = useState(false);
    const [availabilityFilters, setAvailabilityFilters] = useState(new Set(ALL_ACCESS_IDS));
    const [genAiRightsFilters, setGenAiRightsFilters] = useState(new Set(ALL_ACCESS_IDS));
    const [showSelectedOnlyLocal, setShowSelectedOnlyLocal] = useState(false);
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

    // Use parent-controlled state when provided, otherwise local fallback
    const showSelectedOnly = showSelectedOnlyProp !== undefined ? showSelectedOnlyProp : showSelectedOnlyLocal;
    const setShowSelectedOnly = setShowSelectedOnlyProp ?? setShowSelectedOnlyLocal;

    const pluginFilterRef = useRef(null);
    const accessFilterRef = useRef(null);
    const filterDropdownRef = useRef(null);

    const handleRowPluginChange = (resultId, pluginId) => {
        setRowPlugins((prev) => ({ ...prev, [resultId]: pluginId }));
    };

    const handleRowSearch = (e, resultId) => {
        e.stopPropagation();
        const pluginId = rowPlugins[resultId];
        if (!pluginId) return;
        onSearchPlugin?.(resultId, pluginId);
    };

    // Shared cell renderers so the Article Details table and the Abstracts
    // split view render identical Search Plugin / Full Article Access cells.
    const renderPluginCell = (r) => (
        <>
            <div className="position-relative" data-plugin-row={r.id}>
                <button
                    type="button"
                    className="ev-btn-white-fill-round ev-btn-xsmall d-flex align-items-center justify-content-between w-90 ev-btn-border-square ev-med-p-blue mb-1 discovery-plugin-dropdown-btn"
                    onClick={() => setOpenPluginRowId(openPluginRowId === r.id ? null : r.id)}
                >
                    <span className="text-truncate">
                        {PLUGIN_OPTIONS.find((p) => p.id === (rowPlugins[r.id] || r.plugin))?.label || "Select"}
                    </span>
                    <i className={`bi ${openPluginRowId === r.id ? "bi-chevron-up" : "bi-chevron-down"}`} />
                </button>

                {openPluginRowId === r.id && (
                    <div className="ev-card-8 ev-radius-xsmall position-absolute top-100 start-0 mt-1 p-2 z-3 min">
                        {PLUGIN_OPTIONS.map((p) => {
                            const isActive = (rowPlugins[r.id] || r.plugin) === p.id;
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    className={`w-100 text-start ev-radius-xsmall ev-btn-small mb-1 ${isActive
                                        ? "ev-btn-blue-fill-round"
                                        : "ev-btn-light-fill ev-reg-p-blue"
                                        }`}
                                    onClick={() => {
                                        handleRowPluginChange(r.id, p.id);
                                        setOpenPluginRowId(null);
                                    }}
                                >
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="d-flex justify-content-end me-2">
                <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-btn-xsmall d-flex align-items-center gap-1 ev-bold-med"
                    onClick={(e) => handleRowSearch(e, r.id)}
                    disabled={!rowPlugins[r.id] && !r.plugin}
                >
                    <i className="bi bi-search"></i> Search
                </button>
            </div>
        </>
    );

    const renderAccessCell = (r) => (
        <>
            <div className="d-flex align-items-center gap-1 mb-2">
                <span className="ev-xsmall-p-dark text-nowrap fw-semibold">Availability</span>
                <div className="flex-fill">{renderAccessBadge(r.full_text_status, r.full_text_url, onViewPdf, r.title, r.delivery_price, r.id, onGalaxyOrder)}</div>
            </div>
            <div className="d-flex align-items-center gap-1">
                <span className="ev-xsmall-p-dark text-nowrap fw-semibold">Gen AI Rights</span>
                <div className="flex-fill">{renderAccessBadge(r.genai_status, null, null, null, r.genai_price, r.id, onGalaxyOrder, "GENAI")}</div>
            </div>
        </>
    );

    const toggleFilter = (setter, id) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const parseAuthors = (authors) => {
        if (!authors) return [];
        if (Array.isArray(authors)) return authors;
        return authors.split(/;\s*/).map((a) => a.trim()).filter(Boolean);
    };

    const toggleAllFilters = (setter, currentSet) => {
        if (currentSet.size === ALL_ACCESS_IDS.length) {
            setter(new Set());
        } else {
            setter(new Set(ALL_ACCESS_IDS));
        }
    };

    const filteredResults = useMemo(() => {
        return results.filter((r) => {
            if (pluginFilter !== "ALL" && r.plugin !== pluginFilter) return false;

            const avail = r.full_text_status || "UNKNOWN";
            if (availabilityFilters.size < ALL_ACCESS_IDS.length && !availabilityFilters.has(avail)) return false;

            const genAi = r.genai_status || "UNKNOWN";
            if (genAiRightsFilters.size < ALL_ACCESS_IDS.length && !genAiRightsFilters.has(genAi)) return false;

            if (showSelectedOnly && !selectedIds.has(r.id)) return false;

            return true;
        });
    }, [results, pluginFilter, availabilityFilters, genAiRightsFilters, showSelectedOnly, selectedIds]);

    // How many of the currently visible papers are selected
    const filteredSelectedCount = filteredResults.filter(r => selectedIds.has(r.id)).length;

    useEffect(() => {
        if (showSelectedOnly && filteredSelectedCount === 0) {
            setShowSelectedOnly(false);
        }
    }, [showSelectedOnly, filteredSelectedCount]);

    useEffect(() => {
        onFilteredResultsChange?.(filteredResults);
    }, [filteredResults]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pluginFilterRef.current && !pluginFilterRef.current.contains(e.target)) {
                setPluginFilterOpen(false);
            }
            if (accessFilterRef.current && !accessFilterRef.current.contains(e.target)) {
                setAccessFilterOpen(false);
            }
            if (openPluginRowId !== null) {
                const rowDropdown = document.querySelector(`[data-plugin-row="${openPluginRowId}"]`);
                if (rowDropdown && !rowDropdown.contains(e.target)) {
                    setOpenPluginRowId(null);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openPluginRowId]);

    return (
        <div className={fullWidth ? "col-12" : "col-5"}>
            <div className="table-container">
                {/* Scrollable Body */}
                <div className={`scrollable-element overflow-auto ${queryEditorOpen ? "vh-50" : "vh-56"} pe-1`} style={fullWidth ? { overflowX: 'auto' } : undefined}>
                    <table className={`table-custom2 overflow-hidden w-100${fullWidth ? " discovery-table-wide table-fixed" : ""}`}>
                        {fullWidth && (
                            <>
                            <colgroup>
                                <col className="discovery-col-check" />
                                <col className="discovery-col-id" />
                                <col className="discovery-col-pmid" />
                                <col className="discovery-col-authors" />
                                <col className="discovery-col-year" />
                                <col className="discovery-col-title" />
                                <col className="discovery-col-journal" />
                                <col className="discovery-col-vol" />
                                <col className="discovery-col-pages" />
                                {/* discovery-col-ft, discovery-col-access, discovery-col-plugin hidden */}
                                <col className="discovery-col-view" />
                            </colgroup>
                            <thead>
                                <tr className="ev-xsmall-p-dark ev-bold-med">
                                    <th className="discovery-sticky-th"></th>
                                    <th className="discovery-sticky-th">ID</th>
                                    <th className="discovery-sticky-th text-center">PUBMED ID</th>
                                    <th className="discovery-sticky-th text-center">AUTHORS</th>
                                    <th className="discovery-sticky-th text-center">YEAR</th>
                                    <th className="discovery-sticky-th text-center">TITLE</th>
                                    <th className="discovery-sticky-th text-center">JOURNAL</th>
                                    <th className="discovery-sticky-th">VOL</th>
                                    <th className="discovery-sticky-th">PAGES</th>
                                    {/* FULL-TEXT / PDF, GENAI RIGHTS, SEARCH PLUGIN columns hidden */}
                                    <th className="discovery-sticky-th"></th>
                                </tr>
                            </thead>
                            </>
                        )}
                        <tbody>
                {filteredResults.length === 0 ? (
                                <tr>
                                    <td colSpan={fullWidth ? 10 : 3} className="text-center py-5">
                                        {(searchProcessingStatus === null || searchProcessingStatus === "NOT_BEGUN") ? (
                                            <>
                                                <i className="bi bi-search ev-fs-50 text-muted mb-3 d-block"></i>
                                                <p className="ev-reg-p-dark ev-bold-med mb-1">No Search Run Yet</p>
                                                <p className="ev-small-p-dark text-muted mb-3">
                                                    Run your search to discover literature results.
                                                </p>
                                                <button
                                                    type="button"
                                                    className="ev-btn-blue-fill-round ev-btn-reg ev-bold-med d-inline-flex align-items-center gap-2"
                                                    onClick={onRunSearch}
                                                >
                                                    <i className="bi bi-play-fill" />
                                                    Run Search
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-journal-x ev-fs-50 text-muted mb-3 d-block"></i>
                        <p className="ev-reg-p-dark ev-bold-med mb-1">No Literature Results Found</p>
                                                <p className="ev-small-p-dark text-muted mb-0">
                            Try adjusting your search filters or run a new search to find literature.
                        </p>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ) : fullWidth ? (
                                filteredResults.map((r, idx) => (
                                    <tr
                                        key={r.id}
                                        className={`cursor-pointer ${selectedResult?.id === r.id ? "discovery-row-active" : ""}`}
                                        onClick={() => onSelectResult(r)}
                                    >
                                        <td className="p-1">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(r.id)}
                                                onChange={(e) => { e.stopPropagation(); onSelectOne(r.id); }}
                                            />
                                        </td>
                                        <td className="ev-small-p-dark p-1 text-center">{idx + 1}</td>
                                        <td className="p-1 text-center">
                                            <span>{r.pmid || "—"}</span>
                                        </td>
                                        <td className="ev-xsmall-p-dark">
                                            <span className="text-truncate d-inline-block w-100" title={r.authors?.join(", ") || ""}>
                                                {r.authors?.slice(0, 3).join("; ") || "—"}{r.authors?.length > 3 ? ";..." : ""}
                                            </span>
                                        </td>
                                        <td className="ev-small-p-dark text-center p-1">{r.publication_year || "—"}</td>
                                        <td>
                                            <span className="text-truncate d-inline-block w-100" title={r.title}>
                                                {r.title || "—"}
                                            </span>
                                        </td>
                                        <td className="ev-small-p-dark">
                                            <span className="text-truncate d-inline-block w-100" title={r.journal || ""}>
                                                {r.journal || "—"}
                                            </span>
                                        </td>
                                        <td className="ev-small-p-dark p-1 ps-3">{r.volume || "—"}</td>
                                        <td className="ev-small-p-dark p-1 px-3">{r.pages || "—"}</td>
                                        {/* FULL-TEXT/PDF, GENAI RIGHTS, SEARCH PLUGIN cells hidden
                                        <td className="p-1 align-top ev-small-p-dark" onClick={(e) => e.stopPropagation()}>
                                            {renderAccessBadge(r.full_text_status, r.full_text_url, onViewPdf, r.title, r.delivery_price, r.id, onGalaxyOrder)}
                                        </td>
                                        <td className="p-1 align-top ev-small-p-dark" onClick={(e) => e.stopPropagation()}>
                                            {renderAccessBadge(r.genai_status, null, null, null, r.genai_price, r.id, onGalaxyOrder, "GENAI")}
                                        </td>
                                        <td className="p-1 align-top ev-small-p-dark pe-3" onClick={(e) => e.stopPropagation()}>
                                            {renderPluginCell(r)}
                                        </td>
                                        */}
                                        <td className="p-1">
                                            <span className="ev-small-p-blue ev-bold-med cursor-pointer" onClick={(e) => { e.stopPropagation(); onViewArticle ? onViewArticle(r) : onSelectResult(r); }}>
                                                View →
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                filteredResults.map((r) => (
                                    <tr
                                        key={r.id}
                                        className={`cursor-pointer ${selectedResult?.id === r.id ? "discovery-row-active" : ""}`}
                                        onClick={() => onSelectResult(r)}
                                    >
                                        <td className="align-top w-100">
                                            <div className="d-flex align-items-start gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(r.id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        onSelectOne(r.id);
                                                    }}
                                                />
                                                <div>
                                                    <p className="ev-reg-p-dark ev-bold-med w- mb-1">
                                                        {r.title}
                                                    </p>
                                                    <p className="ev-small-p-dark mb-0 fst-italic">
                                                        Authors: {parseAuthors(r.authors).join(", ") || "NA"}
                                                    </p>
                                                    <p className="ev-small-p-dark mb-0">
                                                        Publication Year: {r.publication_year ?? "NA"}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* <td className="align-top w-20" onClick={(e) => e.stopPropagation()}> */}
                                            {/* Search Plugin cell hidden — {renderPluginCell(r)} */}
                                        {/* </td> */}
                                        {/* <td className="align-top w-35"> */}
                                            {/* Availability + GenAI Rights hidden — {renderAccessCell(r)} */}
                                        {/* </td> */}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ResultsList;